/**
 * PlaybookPane — current Playbook view + amendment proposal flow.
 *
 * Renders the 6 constraints + DONTs list from investment_policy.yaml.
 * Amendment proposals flow through the A2A playbook_architect skill
 * (review_amendment) which returns verdict + contrarian_note without
 * mutating state. Actual commits require password-gated
 * /api/v1/playbook/amend (v0.6 wiring).
 */
import { useEffect, useState } from 'react';
import * as api from '../lib/api';

type AmendmentReview = {
  verdict: 'approve' | 'warn' | 'reject';
  issues: string[];
  warnings: string[];
  contrarian_note: string;
  next_steps: string;
  v05_is_heuristic: boolean;
};

export function PlaybookPane() {
  const [pb, setPb] = useState<api.PlaybookCurrent | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'view' | 'propose'>('view');

  useEffect(() => {
    api.morningCoffee.playbookCurrent().then(setPb).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="text-sm text-ink-mute">loading Playbook…</div>;
  }
  if (!pb) {
    return <div className="text-sm text-warn">Playbook unavailable</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-ink-mute">
            policy <span className="num">{pb.policy_version}</span> · intent{' '}
            <span className="num">{pb.intent_version}</span>
          </div>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => setMode('view')}
            className={`btn text-xs ${mode === 'view' ? 'border-accent' : ''}`}
          >
            view
          </button>
          <button
            onClick={() => setMode('propose')}
            className={`btn text-xs ${mode === 'propose' ? 'border-accent' : ''}`}
          >
            propose amendment
          </button>
        </div>
      </div>

      {mode === 'view' && <PlaybookView pb={pb} />}
      {mode === 'propose' && <AmendmentForm />}
    </div>
  );
}

function PlaybookView({ pb }: { pb: api.PlaybookCurrent }) {
  return (
    <div className="space-y-3">
      <div className="card p-3">
        <div className="text-xs text-ink-mute mb-2">
          Constraints ({pb.constraints.length})
        </div>
        <ul className="space-y-2 text-xs">
          {pb.constraints.map((c) => (
            <li key={c.id} className="border-b border-edge pb-2 last:border-b-0">
              <div className="font-semibold">{c.id}</div>
              {c.description && (
                <div className="text-ink-mute">{c.description}</div>
              )}
              {Object.keys(c.params).length > 0 && (
                <pre className="mt-1 text-[10px] bg-bg-elev p-2 rounded num overflow-x-auto">
                  {JSON.stringify(c.params, null, 2)}
                </pre>
              )}
            </li>
          ))}
        </ul>
      </div>
      <div className="card p-3">
        <div className="text-xs text-ink-mute mb-2">DONTs ({pb.donts.length})</div>
        <ul className="space-y-1 text-xs">
          {pb.donts.map((d) => (
            <li key={d} className="font-mono text-warn">
              × {d}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function AmendmentForm() {
  const [yamlText, setYamlText] = useState(
    'constraint:\n  id: <your-new-rule-id>\n  description: <what this rule enforces>\n',
  );
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [review, setReview] = useState<AmendmentReview | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setSubmitting(true);
    setErr(null);
    setReview(null);
    try {
      const resp = (await api.panes.rpc(
        'playbook_architect',
        'review_amendment',
        {
          proposed_yaml: yamlText,
          rationale,
        },
      )) as { result?: AmendmentReview } | AmendmentReview;
      const body =
        'result' in (resp as Record<string, unknown>)
          ? (resp as { result: AmendmentReview }).result
          : (resp as AmendmentReview);
      setReview(body);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="card p-3">
        <label className="text-xs text-ink-mute block mb-1">Proposed YAML</label>
        <textarea
          className="w-full bg-bg-elev border border-edge rounded p-2 num text-xs font-mono"
          rows={8}
          value={yamlText}
          onChange={(e) => setYamlText(e.target.value)}
        />
        <label className="text-xs text-ink-mute block mt-3 mb-1">
          Rationale (≥ 50 chars — what's the asymmetric benefit + what's
          the risk you're accepting?)
        </label>
        <textarea
          className="w-full bg-bg-elev border border-edge rounded p-2 text-xs"
          rows={4}
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
        />
        <div className="flex items-center justify-between mt-3">
          <div className="text-xs text-ink-mute">
            rationale: {rationale.length} chars
          </div>
          <button onClick={submit} disabled={submitting} className="btn text-xs">
            {submitting ? 'reviewing…' : 'submit for review'}
          </button>
        </div>
      </div>

      {err && (
        <div className="card p-3 border-warn text-xs">error: {err}</div>
      )}
      {review && <AmendmentResult review={review} />}
    </div>
  );
}

function AmendmentResult({ review }: { review: AmendmentReview }) {
  const color =
    review.verdict === 'approve'
      ? 'text-accent border-accent'
      : review.verdict === 'warn'
        ? 'border-edge'
        : 'text-warn border-warn';
  return (
    <div className={`card p-3 ${color}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-sm uppercase">
          {review.verdict}
        </div>
        {review.v05_is_heuristic && (
          <span className="pill text-[10px]">heuristic v0.5</span>
        )}
      </div>
      {review.issues.length > 0 && (
        <div className="text-xs mb-2">
          <div className="text-ink-mute mb-1">Issues (blocking):</div>
          <ul className="list-disc list-inside space-y-0.5">
            {review.issues.map((i, k) => (
              <li key={k}>{i}</li>
            ))}
          </ul>
        </div>
      )}
      {review.warnings.length > 0 && (
        <div className="text-xs mb-2">
          <div className="text-ink-mute mb-1">Warnings:</div>
          <ul className="list-disc list-inside space-y-0.5">
            {review.warnings.map((w, k) => (
              <li key={k}>{w}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="text-xs italic mt-3 border-l-2 border-edge pl-2">
        Contrarian note: {review.contrarian_note}
      </div>
      <div className="text-[11px] text-ink-mute mt-3">{review.next_steps}</div>
    </div>
  );
}
